import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ArrowLeft,
  Zap,
  Sparkles,
  Map,
  Lightbulb,
  Shield,
  Heart,
  Github,
  Twitter,
  Linkedin,
  Home,
  Bookmark,
  MapPin,
  Users,
  User,
  Plane,
  Hotel,
  Calendar,
  Globe,
  Brain,
  UserPlus,
  TrendingUp,
  MessageSquare,
  Compass,
  Clock,
  Rocket
} from 'lucide-react';
import FloatingSupport from '../components/FloatingSupport';

// Panchi Bird Logo
const PanchiBirdLogo = ({ className = "w-10 h-10" }) => (
  <img 
    src="https://customer-assets.emergentagent.com/job_panchi-travel-1/artifacts/5hhgrsb0_20260119_170240_0000.png"
    alt="Panchi"
    className={className}
    style={{ objectFit: 'contain' }}
  />
);

const FEATURES = [
  {
    icon: Zap,
    title: '60-Second AI Plans',
    description: 'Complete day-wise itineraries generated by AI in under a minute, personalized to your style',
    color: 'teal'
  },
  {
    icon: Brain,
    title: 'Travel DNA',
    description: 'Build your unique travel profile. Panchi learns your preferences for increasingly personalized trips',
    color: 'rose'
  },
  {
    icon: Plane,
    title: 'Live Flight Search',
    description: 'Real-time flight data powered by Amadeus API with direct booking redirects',
    color: 'cyan'
  },
  {
    icon: Calendar,
    title: 'Smart Event Planning',
    description: 'Plan trips around concerts, festivals, sports events. Never miss IPL, Sunburn, or Cherry Blossoms!',
    color: 'amber'
  },
  {
    icon: Compass,
    title: 'Surprise Me!',
    description: 'Let AI suggest your next adventure based on your Travel DNA and trending destinations',
    color: 'emerald'
  },
  {
    icon: Globe,
    title: 'Global Destinations',
    description: 'Curated hotels and experiences across 50+ destinations worldwide, from Goa to Santorini',
    color: 'orange'
  },
];

const UPCOMING_FEATURES = [
  {
    icon: MessageSquare,
    title: 'Advanced Travel Buddy Experience',
    description: 'In-app chat, group planning, and AI-moderated conversations for safe travel collaboration.',
    status: 'Phase 3',
    color: 'rose'
  },
  {
    icon: Users,
    title: 'Collaborative Itineraries',
    description: 'Joint trip planning with shared edit permissions and AI conflict resolution for group trips.',
    status: 'Phase 3',
    color: 'cyan'
  },
  {
    icon: Plane,
    title: 'Smart Booking Integration',
    description: 'AI-powered booking suggestions with price alerts and affiliate-powered flight & hotel deals.',
    status: 'Phase 3',
    color: 'amber'
  },
  {
    icon: Brain,
    title: 'Persistent Travel Memory',
    description: 'AI remembers your past trips and preferences to create increasingly personalized experiences.',
    status: 'Phase 3',
    color: 'teal'
  },
  {
    icon: Globe,
    title: 'Multi-Language Support',
    description: 'Regional Indian languages with cultural nuance and local festival intelligence.',
    status: 'Phase 3',
    color: 'emerald'
  },
  {
    icon: Shield,
    title: 'Trust & Safety Layer',
    description: 'Verified travelers, reputation system, and AI-assisted safety scoring for secure connections.',
    status: 'Phase 3',
    color: 'orange'
  },
];

export default function About() {
  const navigate = useNavigate();
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-sky-50 via-white to-teal-50 pb-24 md:pb-8">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white/80 backdrop-blur-xl border-b border-gray-100">
        <div className="max-w-4xl mx-auto px-4 md:px-6 py-3 flex items-center justify-between">
          <button 
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-gray-500 hover:text-teal-600 transition-colors"
            data-testid="back-btn"
          >
            <ArrowLeft size={20} />
          </button>
          
          <div className="flex items-center gap-2">
            <PanchiBirdLogo className="w-8 h-8" />
            <span className="font-bold text-gray-900">About</span>
          </div>
          
          <div className="w-8" />
        </div>
      </header>
      
      <main className="max-w-4xl mx-auto px-4 md:px-6 py-8">
        {/* Hero */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-16"
        >
          <motion.div
            animate={{ y: [0, -10, 0], rotate: [0, 5, 0] }}
            transition={{ duration: 4, repeat: Infinity }}
          >
            <PanchiBirdLogo className="w-24 h-24 mx-auto mb-6" />
          </motion.div>
          <h1 className="text-4xl md:text-5xl font-black text-gray-900 mb-4">
            Meet <span className="bg-gradient-to-r from-teal-500 to-cyan-500 bg-clip-text text-transparent">Panchi</span>
          </h1>
          <p className="text-gray-600 text-lg max-w-2xl mx-auto mb-6">
            Your AI-powered travel companion that understands you. We believe every journey should be as unique as the traveler taking it.
          </p>
          <span className="inline-flex items-center gap-2 px-4 py-2 bg-amber-100 text-amber-700 text-sm font-bold" style={{ borderRadius: '9999px' }}>
            <Sparkles size={16} />
            Open Beta
          </span>
        </motion.div>
        
        {/* Features */}
        <div className="mb-16">
          <h2 className="text-2xl font-black text-gray-900 text-center mb-2">
            What Makes Panchi Special
          </h2>
          <p className="text-gray-500 text-center mb-8">Currently live in Open Beta</p>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {FEATURES.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="p-6 bg-white/80 backdrop-blur border border-gray-100 shadow hover:shadow-lg transition-all"
                style={{ borderRadius: '1.25rem' }}
              >
                <div className={`w-12 h-12 flex items-center justify-center mb-4 ${
                  feature.color === 'teal' ? 'bg-teal-100' :
                  feature.color === 'amber' ? 'bg-amber-100' :
                  feature.color === 'cyan' ? 'bg-cyan-100' :
                  feature.color === 'rose' ? 'bg-rose-100' :
                  feature.color === 'emerald' ? 'bg-emerald-100' : 'bg-orange-100'
                }`} style={{ borderRadius: '0.75rem' }}>
                  <feature.icon size={24} className={
                    feature.color === 'teal' ? 'text-teal-600' :
                    feature.color === 'amber' ? 'text-amber-600' :
                    feature.color === 'cyan' ? 'text-cyan-600' :
                    feature.color === 'rose' ? 'text-rose-600' :
                    feature.color === 'emerald' ? 'text-emerald-600' : 'text-orange-600'
                  } />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">{feature.title}</h3>
                <p className="text-gray-600 text-sm">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
        
        {/* Upcoming Features */}
        <div className="mb-16">
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-amber-100 to-orange-100 text-amber-700 text-sm font-bold mb-4" style={{ borderRadius: '9999px' }}>
              <Rocket size={16} />
              Coming Soon
            </div>
            <h2 className="text-2xl font-black text-gray-900">
              What&apos;s Next for Panchi
            </h2>
            <p className="text-gray-500 mt-2">Enhanced features we&apos;re building for you</p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {UPCOMING_FEATURES.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 + index * 0.1 }}
                className="p-6 bg-gradient-to-br from-white to-gray-50 border border-gray-200 shadow hover:shadow-lg transition-all relative overflow-hidden"
                style={{ borderRadius: '1.25rem' }}
              >
                {/* Status Badge */}
                <span className={`absolute top-4 right-4 px-3 py-1 text-xs font-bold ${
                  feature.status === 'Coming Soon' 
                    ? 'bg-amber-100 text-amber-700' 
                    : feature.status === 'Phase 3'
                    ? 'bg-purple-100 text-purple-700'
                    : 'bg-cyan-100 text-cyan-700'
                }`} style={{ borderRadius: '9999px' }}>
                  {feature.status}
                </span>
                
                <div className={`w-12 h-12 flex items-center justify-center mb-4 ${
                  feature.color === 'teal' ? 'bg-teal-100' :
                  feature.color === 'amber' ? 'bg-amber-100' :
                  feature.color === 'cyan' ? 'bg-cyan-100' :
                  feature.color === 'rose' ? 'bg-rose-100' :
                  feature.color === 'emerald' ? 'bg-emerald-100' : 'bg-orange-100'
                }`} style={{ borderRadius: '0.75rem' }}>
                  <feature.icon size={24} className={
                    feature.color === 'teal' ? 'text-teal-600' :
                    feature.color === 'amber' ? 'text-amber-600' :
                    feature.color === 'cyan' ? 'text-cyan-600' :
                    feature.color === 'rose' ? 'text-rose-600' :
                    feature.color === 'emerald' ? 'text-emerald-600' : 'text-orange-600'
                  } />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">{feature.title}</h3>
                <p className="text-gray-600 text-sm">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
        
        {/* Mission */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="p-8 bg-gradient-to-br from-teal-50 to-cyan-50 border border-teal-100 text-center mb-12"
          style={{ borderRadius: '1.5rem' }}
        >
          <h2 className="text-2xl font-black text-gray-900 mb-4">Our Mission</h2>
          <p className="text-gray-600 text-lg max-w-2xl mx-auto">
            To democratize intelligent travel planning by making AI-powered trip creation accessible, personalized, and delightful for every traveler, regardless of their budget or experience level.
          </p>
        </motion.div>
        
        {/* Team */}
        <div className="text-center mb-12">
          <h2 className="text-2xl font-black text-gray-900 mb-6">Built with 💚</h2>
          <div className="flex items-center justify-center gap-2 mb-6">
            <span className="text-4xl">🐦</span>
            <div>
              <p className="font-bold text-gray-900">The Panchi Team</p>
              <p className="text-gray-500 text-sm">Dreamers, Builders, Travelers</p>
            </div>
          </div>
          
          {/* Social Links */}
          <div className="flex items-center justify-center gap-4">
            {[
              { icon: Github, href: '#', label: 'GitHub' },
              { icon: Twitter, href: '#', label: 'Twitter' },
              { icon: Linkedin, href: '#', label: 'LinkedIn' },
            ].map((social) => (
              <a
                key={social.label}
                href={social.href}
                target="_blank"
                rel="noopener noreferrer"
                className="w-12 h-12 bg-gray-100 hover:bg-teal-100 text-gray-600 hover:text-teal-600 flex items-center justify-center transition-all"
                style={{ borderRadius: '50%' }}
              >
                <social.icon size={22} />
              </a>
            ))}
          </div>
        </div>
        
        {/* CTA */}
        <div className="text-center">
          <Link 
            to="/planning"
            className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-teal-500 to-cyan-500 text-white font-bold text-lg shadow-xl shadow-teal-500/30 hover:shadow-teal-500/50 hover:scale-105 transition-all"
            style={{ borderRadius: '9999px' }}
            data-testid="start-planning-btn"
          >
            <Zap size={22} />
            Start Planning
          </Link>
        </div>
      </main>
      
      {/* Mobile Bottom Nav */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 z-50 px-4 pb-4 bg-white border-t border-gray-100">
        <div className="flex items-center justify-around py-2">
          <Link to="/" className="flex flex-col items-center gap-1 p-2 text-gray-400">
            <Home size={22} />
            <span className="text-xs font-medium">Home</span>
          </Link>
          <Link to="/saved" className="flex flex-col items-center gap-1 p-2 text-gray-400">
            <Bookmark size={22} />
            <span className="text-xs font-medium">Saved</span>
          </Link>
          <Link 
            to="/planning"
            className="w-14 h-14 -mt-6 bg-gradient-to-r from-teal-500 to-cyan-500 text-white shadow-lg shadow-teal-500/30 flex items-center justify-center"
            style={{ borderRadius: '50%' }}
          >
            <MapPin size={24} />
          </Link>
          <Link to="/community" className="flex flex-col items-center gap-1 p-2 text-gray-400">
            <Users size={22} />
            <span className="text-xs font-medium">Community</span>
          </Link>
          <Link to="/profile" className="flex flex-col items-center gap-1 p-2 text-gray-400">
            <User size={22} />
            <span className="text-xs font-medium">Profile</span>
          </Link>
        </div>
      </div>
      
      <FloatingSupport />
    </div>
  );
}
